import { css } from "@emotion/react";
import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { CalendarEvent } from "../Calendar";
import { CalendarUI } from "../CalendarUI";
import { FieldProps } from "../FieldBase";
import { FormControl } from "../FormControl";
import { FormHelperText } from "../FieldBase/FormHelperText";
import { InputBase, InputBaseProps } from "../InputBase";
import { InputLabel } from "../InputLabel";
import { Paper } from "../Paper";
import { Popper, PopperProps } from "../Popper";
import { useDitectInputTouched } from "../FieldBase/useDitectInputTouched";
import { useFieldGroup } from "../FieldGroup";
import { useInputValue } from "../hooks/useInputValue";
import { useMergeRefs } from "../hooks/useMergeRefs";
import { useOnClickOutside } from "../hooks";
import { useTheme } from "../theme";
import { useValidation } from "../FieldBase/useValidation";

const today = dayjs();
const todayEvent: CalendarEvent = {
  start: today,
  end: today,
  type: "today",
  props: {
    variant: "outlined",
    color: "primary",
    disableAnimation: true,
  },
};

export const DATE_FORMAT = "YYYY-MM-DD";

export interface DateFieldProps
  extends
    FieldProps<string | undefined>,
    Omit<InputBaseProps, "defaultValue" | "value" | "onChange">,
    Pick<PopperProps, "disablePortal"> {
  type?: never;
  popperProps?: PopperProps;
}

const formatDate = (value?: string) => {
  return value ? dayjs(value).format("YYYY-MM-DD") : undefined;
};

/**
 * ## 入力値のフォーマットについて
 *
 * 入力値のフォーマットは`YYYY-MM-DD`に限定されます。フォーマットするのが面倒な場合は`dayjs`を推奨します。
 * `dayjs`では次のようにしてフォーマットできます。
 *
 * ```tsx
 * dayjs().format("YYYY-MM-DD")
 *```
 */
export const DateField = (props: DateFieldProps) => {
  const {
    className,
    style,
    id,
    label,
    onChange,
    onFocus,
    validationMessages,
    customValidations,
    value: _propsValue,
    disablePortal = true,
    defaultValue,
    min,
    max,
    readOnly: propsReadOnly,
    loading: propsLoading,
    disabled,
    helperText,
    onKeyDown,
    invalid,
    popperProps,
    inputRef,
    ...others
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLInputElement>(null);
  const ref = useRef<HTMLInputElement | null>(undefined);
  const refs = useMergeRefs(inputRef, ref);

  const [value, setValue, isControlled] = useInputValue(props);
  const { readOnly, loading } = useFieldGroup({
    loading: propsLoading,
    readOnly: propsReadOnly,
    onLoaded: () => {
      if (isControlled === false) {
        setValue(defaultValue);
      }
    },
  });

  const validation = useValidation<string | undefined>({
    ref,
    validationMessages,
    customValidations,
    value,
  });

  /**
   * Popper
   */
  const [open, setOpen] = useState(false);
  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);
  const containerRef = useRef(null);
  useOnClickOutside(
    containerRef,
    useCallback(() => {
      setOpen(false);
    }, []),
  );

  /**
   * CalendarUI
   */
  const selectedDate = useMemo(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    () => value || defaultValue,
    [defaultValue, value],
  );
  const selectedEventDay: CalendarEvent[] | undefined = useMemo(() => {
    if (selectedDate === undefined) return undefined;
    return [
      todayEvent,
      {
        start: selectedDate,
        end: selectedDate,
        type: "selected",
        props: {
          variant: "filled",
          color: "primary",
          disableAnimation: true,
        },
      },
    ];
  }, [selectedDate]);
  /**
   * Handling
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.currentTarget.value;
      setValue(nextValue);
      if (onChange) {
        onChange(nextValue, event);
      }
    },
    [onChange, setValue],
  );

  const daysProps = useMemo(() => {
    const handleClickDay = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      const nextValue = formatDate(event.currentTarget.dataset.eventDate);
      setValue(nextValue);
      setOpen(false);
      if (onChange) {
        onChange(nextValue);
      }
    };
    return {
      onClick: handleClickDay,
    };
  }, [onChange, setValue]);

  const handleRef = useCallback(
    (node: HTMLInputElement | null) => {
      setAnchorEl(node);
      refs(node);
    },
    [refs],
  );

  const [touched, additionalProps] = useDitectInputTouched(onFocus);

  const theme = useTheme();

  const internalInputId = useId();
  const inputId = id ?? internalInputId;

  return (
    <FormControl style={style} className={className}>
      <InputLabel
        aria-disabled={disabled}
        required={others.required}
        loading={loading}
        htmlFor={inputId}
      >
        {label}
      </InputLabel>
      <InputBase
        id={inputId}
        onKeyDown={(event) => {
          /**
           * input[type="date"]の場合、Spaceキーでカレンダーを開くことができる。
           * この挙動を無効化する。
           */
          if (event.code === "Space") {
            event.preventDefault();
          }
          onKeyDown?.(event);
        }}
        onChange={handleChange}
        ref={handleRef}
        type="date"
        readOnly={readOnly}
        {...others}
        disabled={disabled}
        onClick={readOnly ? undefined : handleClick}
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        value={value || ""}
        min={min}
        data-invalid={!validation.valid}
        max={max}
        loading={loading}
        {...additionalProps}
      />
      <FormHelperText
        invalid={!validation.valid || invalid}
        loading={loading}
        touched={touched}
        value={value}
      >
        {
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          validation.message || helperText
        }
      </FormHelperText>
      {selectedDate !== "Invalid Date" && (
        <Popper
          disablePortal={disablePortal}
          anchorEl={anchorEl}
          open={open}
          placement="bottom-start"
          options={{
            modifiers: [
              disablePortal
                ? {
                    name: "computeStyles",
                    options: {
                      roundOffsets: ({ y }: { y: number }) => ({
                        x: 0,
                        y: Math.round(y),
                      }),
                    },
                  }
                : {},
              {
                name: "flip",
                enabled: false,
              },
            ],
          }}
          {...popperProps}
        >
          <Paper
            ref={containerRef}
            css={css`
              padding: 1em;
              margin-top: ${theme.spacing}px;
              margin-bottom: ${theme.spacing * 2}px;
            `}
          >
            <CalendarUI
              min={min}
              max={max}
              size="small"
              date={selectedDate}
              events={selectedEventDay}
              daysProps={daysProps}
            />
          </Paper>
        </Popper>
      )}
    </FormControl>
  );
};
